import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useRoles, useAssignRole } from "@/lib/api/roles";
import { useGroups } from "@/lib/api/group";
import { useAbility } from "@/lib/casl/ability-context";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";

interface AssignRoleDialogProps {
  userId: string;
  existingRoleIds: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AssignRoleDialog({
  userId,
  existingRoleIds,
  open,
  onOpenChange,
}: AssignRoleDialogProps) {
  const { data: roles } = useRoles();
  const { data: groups } = useGroups(false);
  const assignRole = useAssignRole(userId);
  const ability = useAbility();
  const canManageAll = ability.can("manage", "all");

  const [selectedRoleId, setSelectedRoleId] = useState<string>("");
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

  // Filter out superadmin role for non-superadmins, and exclude already-assigned roles
  const availableRoles = roles?.filter(
    (r) =>
      !existingRoleIds.includes(r.id) &&
      (canManageAll || r.name !== "superadmin"),
  );

  const handleSubmit = () => {
    if (!selectedRoleId) return;

    const scope =
      selectedGroupIds.length > 0 ? { groups: selectedGroupIds } : undefined;

    assignRole.mutate(
      { roleId: selectedRoleId, scope },
      {
        onSuccess: () => {
          setSelectedRoleId("");
          setSelectedGroupIds([]);
          onOpenChange(false);
        },
      },
    );
  };

  const toggleGroup = (groupId: string) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId],
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign Role</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a role..." />
              </SelectTrigger>
              <SelectContent>
                {availableRoles?.map((role) => (
                  <SelectItem key={role.id} value={role.id}>
                    {role.name}
                    {role.is_system && " (system)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Group Scope (optional)</Label>
            <p className="text-xs text-muted-foreground">
              {canManageAll
                ? "Limit this role to specific groups. Leave empty for unscoped access."
                : "Select at least one group to scope this role."}
            </p>

            {selectedGroupIds.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {selectedGroupIds.map((groupId) => {
                  const group = groups?.find((g) => g.id === groupId);
                  return (
                    <Badge key={groupId} variant="secondary" className="gap-1">
                      {group?.name || groupId}
                      <button onClick={() => toggleGroup(groupId)}>
                        <X className="size-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}

            <Select
              value=""
              onValueChange={(groupId) => {
                if (!selectedGroupIds.includes(groupId)) {
                  toggleGroup(groupId);
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Add group..." />
              </SelectTrigger>
              <SelectContent>
                {groups
                  ?.filter((g) => !selectedGroupIds.includes(g.id))
                  .map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              !selectedRoleId ||
              assignRole.isPending ||
              (!canManageAll && selectedGroupIds.length === 0)
            }
          >
            {assignRole.isPending ? "Assigning..." : "Assign Role"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
